import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import './App.css';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { MdWifiOff } from 'react-icons/md';
import Login from './components/Login/Login';
import PrivacyPolicy from './components/Login/PrivacyPolicy';
import collegeConfig from './config/collegeConfig';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import CollegeAdminDashboard from './components/CollegeAdminDashboard/CollegeAdminDashboard';
import ProgramSwitcher from './components/TeacherDashboard/ProgramSwitcher';
import StudentDashboard from './components/StudentDashboard/StudentDashboard';
import DRPCDashboard from './components/DRPCDashboard/DRPCDashboard';
import DRPInchargeDashboard from './components/DRPInchargeDashboard/DRPInchargeDashboard';
import MonitorDashboard from './components/MonitorDashboard/MonitorDashboard';
import DeanDashboard from './components/DeanDashboard/DeanDashboard';
import UGStudentDashboard from './components/UGStudentDashboard/UGStudentDashboard';

// Create Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function OfflineBanner() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      backgroundColor: '#323232',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '10px 16px',
      fontSize: '13px',
      fontWeight: '500',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
    }}>
      <MdWifiOff size={16} />
      <span>No internet connection</span>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(
    window.location.pathname === '/privacy-policy'
  );
  const verifyToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${collegeConfig.api.baseURL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else if (response.status === 401) {
        // Token is explicitly invalid/expired — clear storage and force re-login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } else {
        // Server error (5xx) or other transient failure — keep session alive using cached data
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try { setUser(JSON.parse(savedUser)); } catch (e) {}
        }
      }
    } catch (error) {
      // Network error — restore session from cached user data so the user isn't logged out
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try { setUser(JSON.parse(savedUser)); } catch (e) {}
      } else {
        console.error('Token verification failed:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Apply theme CSS variables from config
    const root = document.documentElement;
    Object.entries(collegeConfig.theme).forEach(([key, value]) => {
      const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // On native mobile: keep status bar above the app (like PWA behaviour)
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setBackgroundColor({ color: '#ffffff' });
      StatusBar.setStyle({ style: Style.Dark }); // dark icons on white background
    }

    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  // Clear iOS badge whenever the app becomes active
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const clearBadge = () => {
      PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    };

    clearBadge(); // clear on initial open

    let listener;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) clearBadge();
    }).then(l => { listener = l; });

    return () => { listener?.remove(); };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Re-verify token in the background when connectivity is restored
      const token = localStorage.getItem('token');
      if (token) verifyToken(token);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [verifyToken]);

  // Handle navigation based on user type
  const handleNavigate = (userRole) => {
    // Get the user from localStorage (it was set in Login component)
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Show loading state while verifying token
  if (loading) {
    return (
      <div className="app-loading-container">
        <div className="app-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Provide auth context to all components
  return (
    <AuthContext.Provider value={{ user, setUser, logout, isOnline }}>
      <div className="App">
        {!isOnline && <OfflineBanner />}
        {!user ? (
          showPrivacyPolicy ? (
            <PrivacyPolicy onBack={() => {
              window.history.pushState({}, '', '/');
              setShowPrivacyPolicy(false);
            }} />
          ) : (
            <Login onNavigate={handleNavigate} isOnline={isOnline} onViewPrivacy={() => {
              window.history.pushState({}, '', '/privacy-policy');
              setShowPrivacyPolicy(true);
            }} />
          )
        ) : (
          // Render dashboard based on user role
          <>
            {user.role === 'admin' && <AdminDashboard />}
            {user.role === 'college_admin' && <CollegeAdminDashboard />}
            {user.role === 'teacher' && <ProgramSwitcher user={user} />}
            {user.role === 'student' && (
              user.profile?.programLevel === 'UG'
                ? <UGStudentDashboard />
                : <StudentDashboard />
            )}
            {user.role === 'drpc' && <DRPCDashboard />}
            {user.role === 'drp_incharge' && <DRPInchargeDashboard />}
            {user.role === 'monitor' && <MonitorDashboard />}
            {user.role === 'department_monitor' && <MonitorDashboard />}
            {user.role === 'dean' && <DeanDashboard />}
          </>
        )}
      </div>
    </AuthContext.Provider>
  );
}

export default App;